import { DeleteConfirmModal, deletedMessage, SuccessPopup } from "./EventLibrarySelect";
import { CmpEventKindEnum } from "../../../../../graphql/types";
import React, { useId, useMemo, useState } from "react";
import { Checkbox, MenuItem, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import TimeInput from "./EventTimeInput";
import CalendarDateInput from "../../../../common/dashboard/CalendarDateInput";
import VenueCombo, {
  VenueComboOption,
  buildVenueComboOptions,
} from "../../../../common/dashboard/VenueCombo";
import { EventAgendaItem, EventFormState } from "../../eventMockData";
import {
  FieldLabel,
  WizardActionButton,
  WizardStepProps,
  normalizeWizardTimeInput,
  wizardId,
  wizardTextFieldSx,
} from "./wizardShared";
import { SectionShell } from "../../../../common/dashboard/SectionShell";
import { useNonModalSelect } from "../../../../common/dashboard/AnchoredMenu";
import CountrySelectField, {
  CMP_COUNTRY_OPTIONS,
} from "../../../../common/dashboard/CountrySelectField";
import { EventReorderHandle, useEventRowReorder } from "./useEventRowReorder";
import GoogleAddressTextField from "../../../../common/GoogleAddressTextField";

// Options lifted from src/components/common/utilities/ProvincePicker.tsx (it
// does not export them); Canadian provinces first, then US states.
const provinceStateOptions = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];


// Native time inputs emit HH:mm. Agenda rows keep storing display labels like
// "11:00 AM" because the event page renders timeLabel verbatim.
const toTwelveHourLabel = (value: string): string => {
  if (!value) return "";
  const [hours, minutes] = value.split(":").map(Number);
  const meridiem = hours >= 12 ? "PM" : "AM";
  const twelveHour = hours % 12 || 12;
  return `${twelveHour}:${String(minutes).padStart(2, "0")} ${meridiem}`;
};

const FieldError = ({ message }: { message?: string }): React.ReactElement | null =>
  message ? <p className="mt-2 text-sm text-red-600">{message}</p> : null;

// Claude Design (8) Create Event Screen.dc.html:1617, 2206-2209: a missing
// Step 2 field gets the #F0202E border (eb.*), the same red as VenueCombo.
const step2FieldSx = {
  ...wizardTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#F0202E" },
  "& .MuiFormHelperText-root.Mui-error": { color: "#F0202E" },
};

// Venue errors sit under the combo in the same place and size as the MUI
// helper text under Address.
const VenueFieldError = ({ message }: { message?: string }): React.ReactElement | null =>
  message ? <p className="mx-[14px] mt-[3px] text-[12px] leading-[1.66] text-[#F0202E]">{message}</p> : null;

// Claude Design (8) Create Event Screen.dc.html:606-641: each field spans
// three subgrid rows (label and hint, input, error) so inputs line up across
// a row even when one hint wraps to more lines.
const alignedFieldClassName = "row-span-3 grid grid-rows-subgrid content-start gap-y-0";

// Claude Design (8) Create Event Screen.dc.html:576-583, 2495-2496.
const formatCardClassName = (selected: boolean) =>
  `flex items-start gap-[12px] rounded-[10px] border bg-white p-[18px] transition ${
    selected ? "border-cpaGold" : "border-[#ECECEC]"
  }`;

// Event-wizard checkboxes are green (July 9, 2026 direction); the local
// wrapper only adds this step's padding override.
const formatCheckboxSx = { padding: 0.5 };

// Claude Design (8) CLAUDE.md:71 (Create Event Screen.dc.html:55): only the
// box toggles a format. The card is a plain container, so its text and empty
// area are not click targets; the title and description name the box.
const FormatCard = ({
  checked,
  onChange,
  title,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  description: string;
}): React.ReactElement => {
  const id = useId();
  return (
    <div className={formatCardClassName(checked)}>
      <Checkbox
        checked={checked}
        onChange={({ target }) => onChange(target.checked)}
        inputProps={{ "aria-labelledby": `${id}-title`, "aria-describedby": `${id}-description` }}
        sx={formatCheckboxSx}
      />
      <span className="flex flex-col gap-[3px]">
        <span id={`${id}-title`} className="text-[14px] font-semibold leading-5 text-[#0A0A0A]">
          {title}
        </span>
        <span id={`${id}-description`} className="text-[13px] leading-5 text-[#0A0A0A]">
          {description}
        </span>
      </span>
    </div>
  );
};

const trimmed = (value?: string | null): string => (value || "").trim();

// Saved events store province codes ("ON") and the two country names; older
// or imported venues can carry "Ontario" or "canada". Map them onto the
// select options so a picked venue shows a real option.
const toProvinceOptionValue = (value: string): string =>
  provinceStateOptions.find(
    (option) =>
      option.value.toLowerCase() === value.toLowerCase() ||
      option.label.toLowerCase() === value.toLowerCase()
  )?.value || value;
const toCountryOptionValue = (value: string): string =>
  CMP_COUNTRY_OPTIONS.find((country) => country.toLowerCase() === value.toLowerCase()) || value;

// Claude Design (8) Create Event Screen.dc.html:2446-2451: a picked venue sets
// Venue Name and only the address parts it has, so a blank part never clears
// what the user already entered. Step 2 has no Considerations field.
export const applyVenueComboPick = (
  event: EventFormState,
  venue: VenueComboOption
): EventFormState => {
  const next = { ...event, venueName: venue.name };
  if (trimmed(venue.address)) next.address = trimmed(venue.address);
  if (trimmed(venue.city)) next.city = trimmed(venue.city);
  if (trimmed(venue.province)) next.province = toProvinceOptionValue(trimmed(venue.province));
  if (trimmed(venue.postal)) next.postalCode = trimmed(venue.postal);
  if (trimmed(venue.country)) next.country = toCountryOptionValue(trimmed(venue.country));
  return next;
};

// CalendarDateInput emits YYYY-MM-DD for a picked or fully typed date and the
// raw text while a date is still being typed.
const completeDatePattern = /^\d{4}-\d{2}-\d{2}$/;

// YYYY-MM-DD strings compare in calendar order.
export const shouldCopyStartToEnd = (oldStart: string, oldEnd: string, newStart: string) =>
  completeDatePattern.test(newStart) &&
  (!completeDatePattern.test(oldEnd) || oldEnd === oldStart || oldEnd < newStart);

const Step2DateLocation = ({
  event,
  setEvent,
  eventAddressOptions,
  errors,
}: WizardStepProps): React.ReactElement => {
  const community = event.eventKind === CmpEventKindEnum.Community;
  const update = <K extends keyof typeof event>(key: K, value: (typeof event)[K]) =>
    setEvent((prev) => ({ ...prev, [key]: value }));
  // Claude Design (8) CLAUDE.md "Start / end date prefill" (decision Q16,
  // narrowed Sept 28): a Start Date copies to End Date when End is empty,
  // still equals the old Start, or would fall before the new Start. A later
  // End on a multi-day event is kept. Partly typed text is not a date yet, so
  // it leaves End alone.
  const updateStartDate = (value: string) =>
    setEvent((prev) => ({
      ...prev,
      startDate: value,
      endDate: shouldCopyStartToEnd(prev.startDate, prev.endDate, value) ? value : prev.endDate,
    }));
  // Previously entered venues come from the chamber's saved events
  // (getCmpEventAddressOptions, fetched by EventWizard).
  const venueOptions = useMemo(
    () =>
      buildVenueComboOptions(
        (eventAddressOptions || []).map(({ address }) => ({
          name: address.venueName || "",
          address: address.streetAddress,
          city: address.city,
          province: address.province,
          postal: address.postalCode,
          country: address.country,
        }))
      ),
    [eventAddressOptions]
  );

  const agenda = event.agendaItems || [];
  const updateAgenda = (id: string, patch: Partial<EventAgendaItem>) =>
    update(
      "agendaItems",
      agenda.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  // Agenda rows reorder by their "⋮⋮" handle (EVT-CREATE-012, CES:697).
  const reorderAgendaRow = useEventRowReorder(agenda, (items) => update("agendaItems", items));
  const [pendingAgendaRemoval, setPendingAgendaRemoval] = useState<EventAgendaItem | null>(null);
  const provinceSelect = useNonModalSelect();
  const [agendaDeleted, setAgendaDeleted] = useState<ReturnType<typeof deletedMessage> | null>(null);
  const updateAddressField = <K extends keyof typeof event>(key: K, value: (typeof event)[K]) => update(key, value);


  // Date and time apply to every event. A community event hides the format
  // section, so its dates render under Event Location instead (Sept 23 2026:
  // they were hidden with the section and community events could not save).
  const dateTimeFields = (
    <>
      <div className="grid grid-cols-1 gap-[20px] lg:grid-cols-2">
        <div>
          <FieldLabel
            required
            hint="The date when your event will begin. Passed events will be removed from your calendar on your website 30 days after."
          >
            Start Date
          </FieldLabel>
          <CalendarDateInput
            mode="fullDate"
            iconPosition="left"
            appearance="claude"
            ariaLabel="Start Date"
            placeholder="Select Event Start Date"
            value={event.startDate}
            onChange={updateStartDate}
            error={Boolean(errors.startDate)}
          />
          <FieldError message={errors.startDate} />
        </div>
        <div>
          <FieldLabel
            required
            hint="The date when your event will end. Passed events will be removed from your calendar on your website 30 days after."
          >
            End Date
          </FieldLabel>
          <CalendarDateInput
            mode="fullDate"
            iconPosition="left"
            appearance="claude"
            ariaLabel="End Date"
            placeholder="Select Event End Date"
            value={event.endDate}
            onChange={(value) => update("endDate", value)}
            error={Boolean(errors.endDate)}
          />
          <FieldError message={errors.endDate} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-[20px] lg:grid-cols-2">
        <div>
          <FieldLabel required={!community} hint="The time when your event will begin.">
            Start Time
          </FieldLabel>
          {/* Claude Design (8) Create Event Screen.dc.html:666, 671 */}
          <TimeInput
            ariaLabel="Start Time"
            placeholder="Select Start Time"
            value={normalizeWizardTimeInput(event.startTime)}
            onChange={(value) => update("startTime", value)}
            error={Boolean(errors.startTime)}
          />
          <FieldError message={errors.startTime} />
        </div>
        <div>
          <FieldLabel required={!community} hint="The time when your event will end.">
            End Time
          </FieldLabel>
          <TimeInput
            ariaLabel="End Time"
            placeholder="Select End Time"
            value={normalizeWizardTimeInput(event.endTime)}
            onChange={(value) => update("endTime", value)}
            error={Boolean(errors.endTime)}
          />
          <FieldError message={errors.endTime} />
        </div>
      </div>
    </>
  );

  return (
    <>
      {community ? (
        <div className="flex max-w-[560px] flex-col gap-[6px]" data-design-ref="CES:578 EV15-28 max 560 px">
          <FieldLabel hint="Enter the venue name or address where the Community/Member Event takes place. This will appear on the calendar and the event details page.">
            Event Location
          </FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Event Location"
            value={event.venueName}
            onChange={({ target }) => update("venueName", target.value)}
            sx={wizardTextFieldSx}
          />
        </div>
      ) : null}
      {community ? (
        <div className="flex flex-col gap-[20px]">{dateTimeFields}</div>
      ) : null}
      <SectionShell
        id="event-format"
        variant="design"
        title="Select Event Format"
        className={`!border-[#ECECEC] [&_h3]:text-[14px] [&_h3]:font-semibold ${community ? "hidden" : ""}`}
        description="Select one or both formats for your event. You can offer an in-person experience, a virtual experience, or both."
      >
        {community ? null : <>
        {/* Claude Design (8) Create Event Screen.dc.html:573 */}
        {errors.format ? <p className="text-[12px] text-[#F0202E]">{errors.format}</p> : null}
        <div className="grid grid-cols-1 gap-[20px] lg:grid-cols-2">
          <FormatCard
            checked={event.inPersonEnabled}
            onChange={(checked) => update("inPersonEnabled", checked)}
            title="In-person"
            description="This event has a venue or physical location."
          />
          <FormatCard
            checked={event.virtualEnabled}
            onChange={(checked) => update("virtualEnabled", checked)}
            title="Virtual"
            description="Virtual event will require a meeting URL to be entered in text box to allow access"
          />
        </div>

        {event.virtualEnabled ? (
          <div className="max-w-[560px]">
            <FieldLabel required hint="The link attendees will use to join the virtual event.">Meeting URL</FieldLabel>
            <TextField
              size="small"
              fullWidth
              placeholder="https://"
              value={event.virtualAccessInstructions}
              onChange={({ target }) => update("virtualAccessInstructions", target.value)}
              error={Boolean(errors.virtualAccessInstructions)}
              helperText={errors.virtualAccessInstructions}
              sx={step2FieldSx}
            />
          </div>
        ) : null}

        {event.inPersonEnabled ? (
          <>
            <div className="grid grid-cols-1 gap-[20px] lg:grid-cols-2">
              <div className={alignedFieldClassName}>
                <FieldLabel
                  required
                  hint="Enter the name of the venue where the event will take place or select from the dropdown of previously entered venues. This will automatically be displayed on the event page."
                >
                  Venue Name
                </FieldLabel>
                {/* Claude Design (8) Create Event Screen.dc.html:610 */}
                <VenueCombo
                  ariaLabel="Venue Name"
                  placeholder="Venue Name"
                  value={event.venueName}
                  options={venueOptions}
                  onText={(value) => updateAddressField("venueName", value)}
                  onPick={(venue) => setEvent((previous) => applyVenueComboPick(previous, venue))}
                  error={Boolean(errors.venueName)}
                />
                <VenueFieldError message={errors.venueName} />
              </div>
              <div className={alignedFieldClassName}>
                <FieldLabel required hint="The full address of the venue.">
                  Address
                </FieldLabel>
                <GoogleAddressTextField
                  size="small" fullWidth placeholder="Street Number and Name" value={event.address}
                  onValueChange={(value) => updateAddressField("address", value)}
                  onAddressSelect={(address) => setEvent(previous => ({ ...previous,
                    address: address.streetAddress, city: address.city, province: address.provinceCode || address.province,
                    postalCode: address.postalCode, country: address.country || previous.country,
                  }))}
                  error={Boolean(errors.address)} helperText={errors.address} sx={step2FieldSx}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-[20px] md:grid-cols-2 xl:grid-cols-4">
              <div className={alignedFieldClassName}>
                <FieldLabel required hint="City where your event is located">
                  City
                </FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="City"
                  value={event.city || ""}
                  onChange={({ target }) =>
                    updateAddressField("city", target.value)
                  }
                  error={Boolean(errors.city)}
                  helperText={errors.city}
                  sx={step2FieldSx}
                />
              </div>
              <div className={alignedFieldClassName}>
                <FieldLabel required hint="State or province of the venue">
                  Province/State
                </FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  select
                  value={event.province || ""}
                  onChange={({ target }) =>
                    updateAddressField("province", target.value)
                  }
                  error={Boolean(errors.province)}
                  helperText={errors.province}
                  sx={step2FieldSx}
                  SelectProps={{
                    ...provinceSelect.selectProps,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      const value = selected as string;
                      if (!value) {
                        return <span className="text-gray-400">Province/State</span>;
                      }
                      return (
                        provinceStateOptions.find((option) => option.value === value)
                          ?.label || value
                      );
                    },
                  }}
                >
                  {provinceStateOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
              <div className={alignedFieldClassName}>
                <FieldLabel required hint="Postal code of the venue">
                  Postal Code/Zip
                </FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Postal Code/Zip"
                  value={event.postalCode || ""}
                  onChange={({ target }) =>
                    updateAddressField("postalCode", target.value)
                  }
                  error={Boolean(errors.postalCode)}
                  helperText={errors.postalCode}
                  sx={step2FieldSx}
                />
              </div>
              <div className={alignedFieldClassName}>
                <FieldLabel required hint="Country where the event is located">
                  Country
                </FieldLabel>
                {/* IF10 D7: the design Country dropdown (country-select.js);
                    no venue rule limits the country to Canada/United States. */}
                <CountrySelectField
                  id="event-venue-country"
                  size="small"
                  fullWidth
                  value={event.country || ""}
                  onValueChange={(value) => updateAddressField("country", value)}
                  error={Boolean(errors.country)}
                  helperText={errors.country}
                  sx={step2FieldSx}
                  inputProps={{ "aria-label": "Country" }}
                />
              </div>
            </div>
          </>
        ) : null}

        </>}
        {community ? null : dateTimeFields}

        {!community && <>
        <div>
          <h3 className="text-[16px] font-semibold leading-5 text-[#0A0A0A]">
            Create Event Agenda
          </h3>
          <p className="mt-1 text-[14px] leading-5 text-[#0A0A0A]">
            Build your event agenda by adding key activities and their start times. This will be displayed on your event page. If left blank it will not appear.
          </p>
        </div>

        <div className="overflow-x-auto rounded-[10px] border border-[#ECECEC]">
          <div className="grid min-w-[640px] grid-cols-[28px_280px_minmax(0,1fr)_120px] gap-[16px] bg-[#F7F7F8] px-[16px] py-[12px] text-[11px] font-semibold uppercase tracking-wide text-[#0A0A0A]">
            <span /><span>START TIME</span>
            <span>DETAILS</span>
            <span>ACTION</span>
          </div>
          {agenda.map((item, index) => {
            const reorder = reorderAgendaRow(index);
            return (
            <div
              key={item.id}
              {...reorder.rowProps}
              className="grid min-w-[640px] grid-cols-[28px_280px_minmax(0,1fr)_120px] items-center gap-[16px] border-t border-[#ECECEC] px-[16px] py-[12px]"
            >
              <EventReorderHandle {...reorder.handleProps} />
              <TimeInput
                ariaLabel="Agenda start time"
                value={normalizeWizardTimeInput(item.timeLabel)}
                onChange={(value) =>
                  updateAgenda(item.id, { timeLabel: toTwelveHourLabel(value) })
                }
              />
              <TextField
                size="small"
                fullWidth
                value={item.details}
                placeholder="e.g. Registration"
                inputProps={{ "aria-label": "Agenda details" }}
                onChange={({ target }) => updateAgenda(item.id, { details: target.value })}
              />
              <WizardActionButton
                tone="red"
                size="small"
                startIcon={<RemoveCircleOutlineIcon />}
                sx={{ minHeight: 32, fontSize: 11, px: "12px" }}
                onClick={() => setPendingAgendaRemoval(item)}
              >
                Remove
              </WizardActionButton>
            </div>
          );
          })}
          {agenda.length === 0 ? <p className="border-t border-[#ECECEC] px-[16px] py-[20px] text-[14px] text-[#727272]">No agenda items yet. Click Add Agenda to build your event schedule.</p> : null}
        </div>

        <WizardActionButton
          className="!self-start"
          sx={{ minHeight: 38, fontSize: 13, borderRadius: "8px", px: "16px" }}
          tone="gold"
          startIcon={<AddIcon />}
          onClick={() =>
            update("agendaItems", [
              ...agenda,
              { id: wizardId("agenda"), timeLabel: "", details: "" },
            ])
          }
        >
          Add Agenda
        </WizardActionButton>
        </>}
      </SectionShell>
      {/* CES:2625, :1855 (EV15-27): "this agenda item" and a success message. */}
      <DeleteConfirmModal open={Boolean(pendingAgendaRemoval)} heading="Delete Agenda Item"
        name={pendingAgendaRemoval?.details || "this agenda item"} onCancel={() => setPendingAgendaRemoval(null)} onConfirm={() => {
          update("agendaItems", agenda.filter(item => item.id !== pendingAgendaRemoval?.id));
          setAgendaDeleted(deletedMessage("Agenda Item", pendingAgendaRemoval?.details || "this agenda item"));
          setPendingAgendaRemoval(null);
        }} />
      <SuccessPopup
        open={Boolean(agendaDeleted)}
        title={agendaDeleted?.message || ""}
        highlights={agendaDeleted?.highlights}
        onClose={() => setAgendaDeleted(null)}
      />


    </>
  );
};

export default Step2DateLocation;
